import {
  Bell,
  Building2,
  ClipboardList,
  Code2,
  Database,
  FileText,
  Folder,
  Mail,
  MonitorSmartphone,
  PenTool,
  Plug,
  ReceiptText,
  Signature,
  SlidersHorizontal,
  Tag,
  type LucideIcon,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { cn } from "@/lib/utils";

type SettingsNavItem = { label: string; path: string; icon: LucideIcon };
type SettingsNavGroup = { label: string; items: SettingsNavItem[] };

// Grouped, icon-led settings nav (Firm / Workflow / Connect). Paths are the
// same routes as before — only the presentation moved from a horizontal strip
// to this vertical sidebar.
const navGroups: SettingsNavGroup[] = [
  {
    label: "Firm",
    items: [
      { label: "Company", path: "company", icon: Building2 },
      { label: "Letters", path: "letters", icon: FileText },
      { label: "Signatures", path: "signatures", icon: Signature },
      { label: "Invoices", path: "invoices", icon: ReceiptText },
      { label: "Client Portal", path: "portal", icon: MonitorSmartphone },
    ],
  },
  {
    label: "Workflow",
    items: [
      { label: "Defaults", path: "defaults", icon: SlidersHorizontal },
      { label: "Tags", path: "tags", icon: Tag },
      { label: "Folders", path: "folders", icon: Folder },
      { label: "Notifications", path: "notifications", icon: Bell },
      { label: "Notice Templates", path: "notice-templates", icon: ClipboardList },
      { label: "Email Templates", path: "emails", icon: Mail },
    ],
  },
  {
    label: "Connect",
    items: [
      { label: "E-Signature", path: "esignature", icon: PenTool },
      { label: "Integrations", path: "integrations", icon: Plug },
      { label: "API", path: "api", icon: Code2 },
    ],
  },
];

// Ops-only reference-data area — appended to Connect for staff.
const STAFF_ITEM: SettingsNavItem = { label: "Reference Data", path: "reference", icon: Database };

/**
 * Shell for every /settings/* page. A two-column layout: a vertical, grouped
 * settings nav on the left (Firm / Workflow / Connect) beside the "Settings"
 * heading, with each tab its own route so pages stay independently linkable and
 * code-split. Content renders through the `<Outlet />` in the right column.
 */
export function SettingsLayout() {
  const isStaff = useAuthStore((s) => !!s.user?.is_staff);
  const firmName = useAuthStore((s) => s.user?.firm?.name) || "your firm";

  const groups = isStaff
    ? navGroups.map((g) =>
        g.label === "Connect" ? { ...g, items: [...g.items, STAFF_ITEM] } : g,
      )
    : navGroups;

  return (
    <section className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <aside className="flex w-full shrink-0 flex-col gap-3.5 lg:sticky lg:top-6 lg:max-w-[250px]">
        <div className="px-1.5 pt-1">
          <h1 className="text-[28px] leading-tight font-semibold tracking-tight">Settings</h1>
          <p className="text-muted-foreground text-[13px]">How {firmName} shows up and works</p>
        </div>
        <nav className="bg-card shadow-card flex flex-col gap-0.5 rounded-xl border p-2.5">
          {groups.map((group) => (
            <div key={group.label} className="flex flex-col gap-0.5">
              <span className="text-muted-foreground px-3 pt-3 pb-1 text-[11px] font-medium tracking-[0.1em] uppercase first:pt-1">
                {group.label}
              </span>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={`/settings/${item.path}`}
                    className={({ isActive }) =>
                      cn(
                        "flex h-[38px] items-center gap-2.5 rounded-[10px] px-3 text-sm transition-colors",
                        isActive
                          ? "bg-accent text-accent-foreground font-semibold"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted font-medium",
                      )
                    }
                  >
                    <Icon className="size-[15px] shrink-0" />
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </section>
  );
}
